import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { HttpStatusCode } from 'axios';
import z from 'zod';

import { satisfactionOptionsValues } from '@constants/survey.constant';
import { ESurveyEntryPoint } from '@enums/survey.enum';
import { HttpClientError } from '@errors/httpClient.error';
import { isAfterDate } from '@helpers/date/isAfter.helper';
import { qualitySurveyTokenDecoder } from '@helpers/qualitySurveyToken.helper';
import { surveyService } from '@services/surveys/service';

import { QualitySurveyFooter } from './@components/Footer';
import { QualitySurveyHeader } from './@components/Header';

const surveySearchParamsSchema = z.object({
  entry: z.enum(ESurveyEntryPoint).optional(),
  token: z.string().optional(),
});

export const Route = createFileRoute('/_layout/$lang/quality-survey/_layout')({
  component: SurveyLayoutComponent,
  validateSearch: search => surveySearchParamsSchema.parse(search),
  beforeLoad: async ({ location, params, search }) => {
    const isStatusPage =
      location.pathname.includes('expired') ||
      location.pathname.includes('submitted');

    if (isStatusPage) {
      return;
    }

    if (!search.token) {
      throw redirect({
        to: '/$lang/quality-survey/not-found',
        params: { lang: params.lang },
      });
    }

    const decodedToken = qualitySurveyTokenDecoder(search.token);

    if (isAfterDate(decodedToken.exp)) {
      throw redirect({
        to: '/$lang/quality-survey/expired',
        params: { lang: params.lang },
      });
    }

    try {
      const survey = await surveyService.create({
        id: decodedToken.id,
        language: params.lang,
        overallSatisfaction: search.entry
          ? satisfactionOptionsValues[search.entry]
          : undefined,
        parentId: decodedToken.parentId,
        source: decodedToken.source,
        triggerDate: decodedToken.triggerDate,
        ttl: decodedToken.exp,
        type: decodedToken.type,
      });

      if (survey.submitted) {
        throw redirect({
          to: '/$lang/quality-survey/submitted',
          params: { lang: params.lang },
        });
      }
    } catch (error) {
      console.log('error', error);
      if (
        error instanceof HttpClientError &&
        error.status === HttpStatusCode.Gone
      ) {
        throw redirect({
          to: '/$lang/quality-survey/expired',
          params: { lang: params.lang },
        });
      }

      throw error;
    }
  },
});

function SurveyLayoutComponent() {
  return (
    <div className="grid min-h-screen grid-rows-[auto_1fr_auto]">
      <QualitySurveyHeader />
      <Outlet />
      <QualitySurveyFooter />
    </div>
  );
}
